import { useEffect } from "react";
import { Button, Division, DropZone, Flex, Form, Icon, Icons, Paragraph, Select, Spin, useToast } from "@ir/ui";
import classNames from "classnames";
import { DocumentMimeType } from "@/core/enums/DocumentMimeType";
import { useDeductionDocumentUpload } from "./hook";
import { Deduction } from "@/core/entities/deduction";
import { IDeductionAttachDocumentsParams } from "@/core/repositories/deduction";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { DeductionRepository } from "@/infrastructure/repositories/deduction";
import { useDeductionDrawer } from "@/presentation/deduction/hooks/drawer/useDeductionDrawer";
import { useLookup } from "@/hooks";
import AttachDeductionDocumentsUseCase from "@/core/usecases/deduction/AttachDeductionDocuments.useCase";
import { DeductionUploadDocument } from "./atom/documentUpload";
import { useAtomValue } from "jotai";
import { groupByCheckAtom } from "@/store/deduction/main";
import { currentPageAtom as currentRemittancePageAtom } from "@/store/deduction/remittance";
import { currentPageAtom as currentRemittanceItemPageAtom } from "@/store/deduction/remittanceItem";

// Constants
const FILE_CONSTANTS = {
  MAX_LENGTH: 10,
  MAX_SIZE_MB: 2,
  MAX_FILE_NAME_LENGTH: 20,
  ACCEPTED_TYPES: [
    DocumentMimeType.PDF,
    DocumentMimeType.EXCEL,
    DocumentMimeType.EXCEL_LEGACY,
    DocumentMimeType.CSV,
    DocumentMimeType.PLAIN_TEXT,
    DocumentMimeType.WORD,
    DocumentMimeType.WORD_DOCX,
    DocumentMimeType.JPEG,
    DocumentMimeType.PNG,
    DocumentMimeType.GIF,
    DocumentMimeType.TIFF,
    DocumentMimeType.BMP,
    DocumentMimeType.EDI,
  ],
  ACCEPTED_EXTENSIONS: ["pdf", "xlsx", "xls", "csv", "txt", "doc", "docx", "jpg", "png", "gif", "tiff", "bmp", "edi"],
};

// Types
interface IDocumentUploadFormProps {
  attachDocumentTrigger: number | null;
  deduction: Deduction;
  onClose: () => void;
  isDocumentAttached: boolean;
  setIsDocumentAttached: (isDocumentAttached: boolean) => void;
}

interface IFormDocument {
  processId: number;
  documentType: { label: string; value: string } | null;
}

interface IFormData {
  documents: IFormDocument[];
}

// Utility functions
const getFileType = (file: File): DocumentMimeType => {
  const isEdiFile = file.name?.toLowerCase().endsWith(".edi");
  return isEdiFile ? DocumentMimeType.EDI : (file.type.toLowerCase() as DocumentMimeType);
};

const sanitizeFileName = (filename: string): string => {
  const sanitized = filename
    .replaceAll(/[<>:"/\\|?*]/g, "")
    .replaceAll(/\s+/g, "_")
    .replaceAll(/[^a-zA-Z0-9._-]/g, "")
    .replaceAll(/^\.+/g, "")
    .replaceAll(/\.{2,}/g, ".")
    .trim();

  return sanitized || "untitled";
};

const getFileSize = (file: File): number => file.size / 1024 / 1024; // Returns size in MB

const getFileSizeInString = (file: File): string => {
  const sizeInMB = getFileSize(file);
  return sizeInMB < 1 ? `${(file.size / 1024).toFixed(2)} KB` : `${sizeInMB.toFixed(2)} MB`;
};

const validateFile = (file: File): { error: string | null; isValid: boolean } => {
  const fileSize = getFileSize(file);

  if (fileSize > FILE_CONSTANTS.MAX_SIZE_MB) {
    return { error: `File size must be less than ${FILE_CONSTANTS.MAX_SIZE_MB}MB`, isValid: false };
  }

  if (!FILE_CONSTANTS.ACCEPTED_TYPES.includes(getFileType(file))) {
    return { error: "File type is not supported", isValid: false };
  }

  return { error: null, isValid: true };
};

const DocumentUploadForm = ({ attachDocumentTrigger, deduction, onClose, isDocumentAttached, setIsDocumentAttached }: IDocumentUploadFormProps) => {
  const [deductionUploadForm] = Form.useForm<IFormData>();
  const { error: showErrorToast } = useToast();
  const { documentTypesLookup, getDocumentTypesLookup } = useLookup();
  const { documents, addDocumentUpload, uploadDocument, removeDocument, setIsAttaching, clearDocumentUpload, updateDeduction } = useDeductionDocumentUpload();
  const { setViewerDocuments } = useDeductionDrawer();

  const groupByCheck = useAtomValue(groupByCheckAtom);
  const currentRemittancePage = useAtomValue(currentRemittancePageAtom);
  const currentRemittanceItemPage = useAtomValue(currentRemittanceItemPageAtom);
  const currentPage = groupByCheck ? currentRemittancePage : currentRemittanceItemPage;

  useEffect(() => {
    getDocumentTypesLookup();
  }, [getDocumentTypesLookup]);

  const handleOnDrop = async (files: File[]) => {
    const currentFileLength = documents.length;
    const filesToUpload = files.slice(0, FILE_CONSTANTS.MAX_LENGTH - currentFileLength);

    await addDocumentUpload(
      filesToUpload.map((file: File, index: number) => {
        const newFile = new File([file], sanitizeFileName(file.name), { type: getFileType(file) });

        const { error, isValid } = validateFile(newFile);

        return {
          processId: Date.now() + index,
          file: newFile,
          isPending: isValid,
          isUploading: false,
          isUploaded: false,
          error,
          response: null,
          type: null,
        };
      })
    );

    await uploadDocument();
  };

  useEffect(() => {
    if (attachDocumentTrigger) {
      deductionUploadForm.submit();
    }
  }, [attachDocumentTrigger, deductionUploadForm]);

  useEffect(() => {
    return () => {
      deductionUploadForm.resetFields();
      clearDocumentUpload();
      setIsDocumentAttached(false);
    };
  }, [clearDocumentUpload, setIsDocumentAttached, deductionUploadForm]);

  const doAttachDocuments = async () => {
    setIsAttaching(true);
    const responseDocuments = documents.map(document => {
      return document.response?.data.document ?? null;
    });
    const currentFormDocuments: IFormDocument[] = deductionUploadForm.getFieldValue(["documents"]);

    const attachDocumentsRequest: IDeductionAttachDocumentsParams = {
      deduction_id: deduction.id,
      files: responseDocuments
        .filter(document => Boolean(document?.fileName))
        .map((document, index) => {
          const currentFormDocument =
            currentFormDocuments.find((formDocuments: IFormDocument) => formDocuments.processId === documents[index].processId) ?? null;
          const responseDocument = document!;
          const currentDocumentType = currentFormDocument?.documentType ?? null;
          return {
            document_type_id: Number.parseInt(currentDocumentType?.value ?? ""),
            file_name: responseDocument.fileName,
            file_display_name: responseDocument.displayName,
            mime_type: responseDocument.mimeType,
            size: responseDocument.size,
          };
        })
        .filter(document => Boolean(document.document_type_id)),
    };

    try {
      const client = GraphQlAdapter.getInstance().getClient();
      const attachDocumentsToDeductionUseCase = new AttachDeductionDocumentsUseCase(new DeductionRepository(client));
      const response = await attachDocumentsToDeductionUseCase.execute(attachDocumentsRequest, currentPage);

      updateDeduction(response.attachDocument);
      setIsDocumentAttached(true);
      setViewerDocuments(response.attachDocument.documents);
      setTimeout(onClose, 1000);
    } catch {
      showErrorToast({
        content: "Failed to attach documents",
      });
    } finally {
      setIsAttaching(false);
    }
  };

  const removeDocumentFromForm = (processId: number) => {
    const currentFormDocuments = deductionUploadForm.getFieldValue(["documents"]);
    deductionUploadForm.setFieldsValue({
      documents: currentFormDocuments.filter((d: IFormDocument) => d.processId !== processId),
    });
    removeDocument(processId);
  };

  const getSortedDocuments = (documents: DeductionUploadDocument[]) => {
    return documents.reverse();
  };

  useEffect(() => {
    const oldFormDocuments = deductionUploadForm.getFieldValue(["documents"]) ?? [];
    const newFormDocuments = [...oldFormDocuments];
    const sortedDocuments = getSortedDocuments(documents);
    for (const d of sortedDocuments) {
      if (!newFormDocuments.some(f => f.processId === d.processId)) {
        newFormDocuments.unshift({
          processId: d.processId,
          documentType: null,
        });
      }
    }

    deductionUploadForm.setFieldsValue({
      documents: newFormDocuments,
    });
  }, [documents, deductionUploadForm]);

  const renderDocumentItem = (eachField: any, currentDocument: any) => {
    if (!currentDocument) return null;

    const fileName = currentDocument?.file.name;
    const extension = fileName?.split(".").pop();
    const nameWithoutExtension = fileName?.substring(0, fileName?.lastIndexOf("."));
    const displayName =
      nameWithoutExtension && nameWithoutExtension.length > FILE_CONSTANTS.MAX_FILE_NAME_LENGTH
        ? `${nameWithoutExtension.substring(0, FILE_CONSTANTS.MAX_FILE_NAME_LENGTH)}...${extension}`
        : fileName;

    return (
      <Flex
        key={eachField.name}
        direction="column"
        align="stretch"
        className={classNames("p-0 border-1 border-color-grey-50 mt-2", {
          "border-color-error-500": currentDocument.error,
          "border-color-success-500": currentDocument.isUploaded,
          "border-color-warning-500": currentDocument.isPending && !currentDocument.error && !currentDocument.isUploading,
        })}
        style={{ borderRadius: 8 }}
      >
        <Flex direction="row" gap={1} align="center" className="p-2 border-bottom-1 border-color-grey-50">
          <Flex align="center" justifyContent="center">
            <Icon name={Icons.File} color={currentDocument.error ? "error" : "grey"} />
          </Flex>
          <Flex className="flex-1" direction="column">
            <Paragraph className="m-0 paragraph text-grey-700 text-md" style={{ whiteSpace: "nowrap" }} title={fileName}>
              {displayName}
            </Paragraph>
            {currentDocument?.error && <Paragraph className="text-xs text-error-500 m-0">{currentDocument.error}</Paragraph>}
            <Paragraph className="text-xs text-grey-400 m-0">{getFileSizeInString(currentDocument?.file)}</Paragraph>
          </Flex>
          <Flex align="center" justifyContent="center">
            <Spin size="medium" spinning={currentDocument.isUploading} />
          </Flex>
          <Flex align="center" justifyContent="center">
            <Button
              isDisabled={currentDocument.isUploading ?? isDocumentAttached}
              variant="clear"
              color="grey"
              onPress={() => removeDocumentFromForm(currentDocument.processId)}
            >
              <Icon name={Icons.Close} color="grey" />
            </Button>
          </Flex>
        </Flex>
        {!currentDocument?.error && (
          <Flex className="p-2">
            <Form.Item
              name={[eachField.name, "documentType"]}
              label="Document type"
              rules={[{ required: !currentDocument?.error, message: "Document type is required" }]}
              className="w-100 mb-0"
            >
              <Select
                size="medium"
                menuPlacement="top"
                isClearable={false}
                isSearchable={false}
                options={documentTypesLookup?.data?.map(deductionDocumentType => ({
                  label: deductionDocumentType.typeName,
                  value: deductionDocumentType.id.toString(),
                }))}
                isLoading={documentTypesLookup.isLoading}
              />
            </Form.Item>
          </Flex>
        )}
      </Flex>
    );
  };

  return (
    <Form form={deductionUploadForm} onFinish={doAttachDocuments} className="m-0">
      <DropZone
        onDrop={handleOnDrop}
        description={
          <Paragraph>
            Accepted file types: <b>{FILE_CONSTANTS.ACCEPTED_EXTENSIONS.join(", ")}</b> only. You can upload upto {FILE_CONSTANTS.MAX_LENGTH} files, with a
            maximum size of {FILE_CONSTANTS.MAX_SIZE_MB}MB per file
          </Paragraph>
        }
        allowedFileTypesOnSelect={FILE_CONSTANTS.ACCEPTED_TYPES}
        isDisabled={documents.length >= FILE_CONSTANTS.MAX_LENGTH || documents.some(d => d.isPending)}
        overlay={isDocumentAttached && <Icon name={Icons.CheckCircle} color="grey" style={{ width: "45px", height: "45px" }} className="text-success-500" />}
        overlayClassName="bg-grey-75-transparent"
      />

      <Division>
        <Form.List
          name="documents"
            rules={[
            {
              validator: async (_, list) => {
                if (!list || list.length < 1) {
                  throw new Error("At least 1 document is required");
                }
              },
            },
          ]}
        >
          {(fields, _, { errors }) => (
            <>
              {fields.map(eachField => {
                const currentFormDocument = deductionUploadForm.getFieldValue(["documents", eachField.name]);
                const currentDocument = documents.find(d => d.processId === currentFormDocument.processId);
                return renderDocumentItem(eachField, currentDocument);
              })}
              <Form.ItemError>{errors}</Form.ItemError>
            </>
          )}
        </Form.List>
      </Division>
    </Form>
  );
};

export default DocumentUploadForm;
